/* eslint-disable @typescript-eslint/camelcase */
import moment from 'moment';
import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useSet } from 'react-use';
import { useTheme } from 'styled-components';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { ErrorState } from '../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { Icon } from '../../../../CommonComponents/OPL/Icon/Icon';
import { IconButton } from '../../../../CommonComponents/OPL/IconButton/IconButton';
import {
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  TableTitle,
  TableWrapper,
} from '../../../../CommonComponents/OPL/Table/Table';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import {
  hasAccessToLinkAccountsSelector,
  hasPlaidSelector,
  selectAkoyaPhase,
} from '../../../../CommonSelectors/CommonAreaSelector';
import { formatCurrency } from '../../../../CommonUtils/formatNumber';
import { PlaidLinkType, usePlaidLink } from '../../../../_hooks/plaid/usePlaidLink';
import { ACCOUNTS_QUERY_KEY } from '../../AccountsConstants';
import { ACCOUNT_PREFERENCES_QUERY_KEY } from '../../api/useAccountsQuery';
import { SimpleLinkStatusDTO } from '../../api/useLinksQuery';
import { AccountsPopover } from '../../components/AccountsPopover/AccountsPopover';
import { ClickableRow } from '../../components/ClickableRow/ClickableRow';
import { DeleteModal } from './components/DeleteModal/DeleteModal';
import { DetailsModal } from './components/DetailsModal/DetailsModal';
import { LoadingTable } from './components/LoadingTable/LoadingTable';
import { Institution, useManageLinks } from './useManageLinks';
import { QuovoDisabledMessageModal } from '../../../../Quovo/components/QuovoDisabledMessage/QuovoDisabledMessageModal';
import { setIsPlaidInfoMessageVisibility } from '../../../../Plaid/state/actions';
import { PlaidLinkInfoAlert } from '../../../../Plaid/components/PlaidLinkInfoAlert/PlaidLinkInfoAlert';
import { useAccountContext } from '../../context/useAccountContext';
import { formatDate } from '../../../../CommonUtils/formatDate';
import { Dot } from '../../../../CommonComponents/OPL/Dot/Dot';
import { AddAccountPreSelectionModal } from '../AccountPreferences/components/AddAccountPreSelectionModal/AddAccountPreSelectionModal';

enum ModalType {
  Details = 'details',
  Delete = 'delete',
}

interface Props {
  clientId: string;
  entityId?: string;
}

export const ManageLinks: React.FC<Props> = ({ clientId, entityId }) => {
  const queryClient = useQueryClient();
  const theme = useTheme();

  const dispatch = useDispatch();

  const [showQuovoMessage, setShowQuovoMessage] = useState(false);
  const [isAddAccountPreSelectionModalOpen, setIsAddAccountPreSelectionModalOpen] = useState(false);

  const [, { has: hasLinkIdSelected, add: selectLinkId, remove: deleteSelectedLinkId }] = useSet(
    new Set([entityId]),
  );

  const { isWorkflow } = useAccountContext();

  const {
    data,
    isLoading,
    isError,
    error: accountLinksError,
    refetch,
    isFetching,
  } = useManageLinks();

  const [selectedInstitution, setSelectedInstitution] = useState<Institution | null>(null);
  const [selectedModalType, setSelectedModalType] = useState<ModalType | null>(null);

  const {
    open: openPlaidModal,
    isLoading: isLoadingPlaidUpdateLink,
    queryError: openPlaidError,
    mutationError: upsertLinkError,
  } = usePlaidLink({
    clientId,
    onSuccess: (metadata, response, linkType) => {
      if (linkType === PlaidLinkType.Create) {
        dispatch(setIsPlaidInfoMessageVisibility(true));
      }

      refetch();

      queryClient.invalidateQueries([ACCOUNTS_QUERY_KEY, ACCOUNT_PREFERENCES_QUERY_KEY]);
    },
  });

  const hasPlaidAccess = useSelector(hasPlaidSelector);

  const hasAccessToLinkAccounts = useSelector(hasAccessToLinkAccountsSelector);

  const akoyaPhase = useSelector(selectAkoyaPhase);

  const hasAnyLinkingMethod = hasPlaidAccess || akoyaPhase !== 'none';

  if (isLoading || isLoadingPlaidUpdateLink || (accountLinksError && isFetching)) {
    return <LoadingTable isWorkflow={isWorkflow} />;
  }

  if (isError) {
    return (
      <Box
        display="flex"
        width="100%"
        height="80%"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        my="56"
      >
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const handleFixLink = (institution: Institution) => {
    if (hasPlaidAccess) {
      openPlaidModal(institution.ExternalLinkId);
    } else {
      setShowQuovoMessage(true);
    }
  };

  const someLinksExpire = data.some(i => !!i.ExpirationDate);

  return (
    <>
      {hasAccessToLinkAccounts && hasAnyLinkingMethod && (
        <Box display="flex" justifyContent="flex-end" alignItems="center" mt="24">
          <Button
            leftIcon="far fa-link"
            onClick={() => {
              setIsAddAccountPreSelectionModalOpen(true);
            }}
          >
            Link Institution
          </Button>
        </Box>
      )}

      {(openPlaidError || upsertLinkError) && (
        <Alert
          mt="24"
          message={
            openPlaidError?.toString() ||
            upsertLinkError?.toString() ||
            'Sorry, something went wrong. Please try again.'
          }
          title={openPlaidError?.name || upsertLinkError?.name}
          variant="error"
        />
      )}

      <PlaidLinkInfoAlert mt="24" />

      {data &&
        data
          .filter(institution => institution.SimpleLinkStatus === SimpleLinkStatusDTO.Broken)
          .map(institution => (
            <Alert
              key={institution.ExternalLinkId}
              mt="24"
              title={`${institution.InstitutionName} Disconnected`}
              message="This account was disconnected. Values displayed will be as of that date."
              variant="warning"
              extraAction={{
                text: 'Fix Connection',
                action: () => handleFixLink(institution),
              }}
            />
          ))}

      {data && (
        <TableWrapper mt="24" variant={isWorkflow ? 'bordered' : undefined}>
          <TableTitle>
            <Text fontWeight={600} fontSize="h6" pl="16">
              Institutions
            </Text>
          </TableTitle>
          {data?.length === 0 ? (
            <TableBody>
              <TableRow>
                <TableCell width="100%">
                  <Text fontSize="small" color="neutral.600">
                    You have not added any accounts
                  </Text>
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <>
              <TableHeader>
                <TableRow pl="32">
                  <TableCell width="30%">
                    <Text variant="tableHeader" style={{ color: theme.colors.neutral[700] }}>Name</Text>
                  </TableCell>
                  <TableCell width="20%">
                    <Text variant="tableHeader" style={{ color: theme.colors.neutral[700] }} textAlign="right" width="100%">
                      Balance
                    </Text>
                  </TableCell>
                  <TableCell width="15%">
                    <Text variant="tableHeader" style={{ color: theme.colors.neutral[700] }}>Last Update</Text>
                  </TableCell>
                  {someLinksExpire && (
                    <>
                      <TableCell width="10%">
                        <Text variant="tableHeader" style={{ color: theme.colors.neutral[700] }}>Expiration</Text>
                      </TableCell>
                      <TableCell width="15%">
                        <Text variant="tableHeader" style={{ color: theme.colors.neutral[700] }}>Status</Text>
                      </TableCell>
                    </>
                  )}
                  <TableCell width="10%" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.map(institution => {
                  const lastGoodSyncUTC = moment.utc(institution.LastGoodSync);
                  const clientDateUTC = moment.utc().format();
                  const expiresSoon =
                    institution.ExpirationDate &&
                    moment(institution.ExpirationDate).isBefore(moment().add(30, 'days'));

                  return (
                    <React.Fragment key={institution.ExternalLinkId}>
                      <ClickableRow
                        isActive={hasLinkIdSelected(institution.ExternalLinkId)}
                        pl="32"
                        onClick={() => {
                          if (institution.Accounts.length > 0) {
                            if (hasLinkIdSelected(institution.ExternalLinkId)) {
                              deleteSelectedLinkId(institution.ExternalLinkId);
                            } else {
                              selectLinkId(institution.ExternalLinkId);
                            }
                          }
                        }}
                      >
                        <TableCell width="30%">
                          {institution.Accounts.length > 0 && (
                            <Box width="16px" display="flex" mr="8">
                              <Icon
                                width="100%"
                                className={`${
                                  hasLinkIdSelected(institution.ExternalLinkId)
                                    ? 'fas fa-chevron-down'
                                    : 'fas fa-chevron-right'
                                }`}
                                color="neutral.400"
                              />
                            </Box>
                          )}
                          <Text fontWeight={500} color="neutral.700">
                            {institution.InstitutionName}
                          </Text>
                        </TableCell>
                        <TableCell width="20%">
                          <Text color="neutral.700" fontWeight={500} textAlign="right" width="100%">
                            {institution.SimpleLinkStatus === SimpleLinkStatusDTO.Pending &&
                            institution.Balance === 0
                              ? ''
                              : formatCurrency(Math.round(institution.Balance))}
                          </Text>
                        </TableCell>
                        <TableCell width="15%">
                          {institution.LastGoodSync && (
                            <Text fontWeight={500} color="neutral.700">
                              {lastGoodSyncUTC.from(clientDateUTC)}
                            </Text>
                          )}
                        </TableCell>

                        {someLinksExpire && (
                          <>
                            <TableCell width="10%">
                              {institution.ExpirationDate ? (
                                <Text fontWeight={500} color="neutral.700">
                                  {formatDate(institution.ExpirationDate)}
                                </Text>
                              ) : (
                                '–'
                              )}
                            </TableCell>
                            <TableCell width="15%">
                              <Box
                                display="flex"
                                alignItems="center"
                                css={({ theme }) => ({
                                  gap: theme.space[4],
                                })}
                              >
                                <Dot bg={expiresSoon ? 'warning.400' : 'success.500'} />
                                <Text fontWeight={500} color="neutral.700">
                                  {expiresSoon ? 'Expiring Soon' : 'Active'}
                                </Text>
                              </Box>
                            </TableCell>
                          </>
                        )}

                        <TableCell width="10%">
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="flex-end"
                            width="100%"
                          >
                            {SimpleLinkStatusDTO.Pending === institution.SimpleLinkStatus && (
                              <AccountsPopover
                                title="Account Pending"
                                description="Check back in 24h."
                                ariaLabel="Account pending - check back in 24h"
                              >
                                <Icon className="fas fa-exclamation-triangle" color="warning.300" />
                              </AccountsPopover>
                            )}
                            {SimpleLinkStatusDTO.Broken === institution.SimpleLinkStatus && (
                              <AccountsPopover
                                title={`${institution.InstitutionName} Disconnected`}
                                description="This account was disconnected. Values displayed will be as of that date."
                                buttonText="Fix Connection"
                                onClick={() => handleFixLink(institution)}
                                ariaLabel={`${institution.InstitutionName} disconnected - fix connection`}
                              >
                                <Icon className="fas fa-exclamation-triangle" color="warning.300" />
                              </AccountsPopover>
                            )}
                            {hasPlaidAccess && (
                              <IconButton
                                aria-label="Plaid support details"
                                ml="4"
                                icon="far fa-info-circle"
                                variant="subtleInactive"
                                onClick={() => {
                                  setSelectedModalType(ModalType.Details);
                                  setSelectedInstitution(institution);
                                }}
                              />
                            )}

                            {!institution.IsReadOnly && (
                              <IconButton
                                aria-label={`Unlink ${institution.InstitutionName}`}
                                ml="4"
                                icon="far fa-trash-alt"
                                variant="subtleInactive"
                                onClick={() => {
                                  setSelectedModalType(ModalType.Delete);
                                  setSelectedInstitution(institution);
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                      </ClickableRow>

                      {hasLinkIdSelected(institution.ExternalLinkId) &&
                        institution.Accounts.map(account => (
                          <TableRow key={account.AccountId} pl="32">
                            <TableCell width="30%">
                              <Text color="neutral.700">{account.Description}</Text>
                            </TableCell>
                            <TableCell width="20%">
                              <Text color="neutral.700" textAlign="right" width="100%">
                                {formatCurrency(account.Balance)}
                              </Text>
                            </TableCell>
                            <TableCell width="15%"></TableCell>
                            {someLinksExpire && (
                              <>
                                <TableCell width="10%"></TableCell>
                                <TableCell width="15%"></TableCell>
                              </>
                            )}

                            <TableCell width="10%"></TableCell>
                          </TableRow>
                        ))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </>
          )}
        </TableWrapper>
      )}

      {selectedModalType === ModalType.Delete && (
        <DeleteModal
          clientId={clientId}
          institution={selectedInstitution}
          onClose={() => setSelectedInstitution(null)}
        />
      )}

      {selectedModalType === ModalType.Details && (
        <DetailsModal
          institution={selectedInstitution}
          onClose={() => setSelectedInstitution(null)}
        />
      )}

      {showQuovoMessage && (
        <QuovoDisabledMessageModal onRequestClose={() => setShowQuovoMessage(false)} />
      )}

      {isAddAccountPreSelectionModalOpen && (
        <AddAccountPreSelectionModal
          showExternalProviders
          onClose={() => {
            setIsAddAccountPreSelectionModalOpen(false);
          }}
          showQuovoMessage={() => setShowQuovoMessage(true)}
          onOpenAkoyaLinkManagementModal={() => {
            setIsAddAccountPreSelectionModalOpen(false);
          }}
        />
      )}
    </>
  );
};
